import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useAuth } from '@opendoor/auth-fe/context';
import { redactGraphqlRequestErrorForTelemetry } from 'components/api/graphqlRequestError';
import {
  requestZeusFromBrowser,
  requestZeusReadFromBrowser,
} from 'components/api/zeusBrowserClient';
import type { TypedDocumentString, Variables } from 'graphql-request';
import { captureError } from 'helpers/captureError';

interface UseMortgageQueryOptions<TVariables> {
  variables: TVariables | null;
  skip?: boolean;
  failurePolicy?: 'retry-and-report' | 'single-attempt-silent';
}

const MORTGAGE_READ_TIMEOUT_MS = 15_000;

export function useMortgageQuery<TData, TVariables extends Variables>(
  document: TypedDocumentString<TData, TVariables>,
  {
    variables,
    skip = false,
    failurePolicy = 'retry-and-report',
  }: UseMortgageQueryOptions<TVariables>,
) {
  const { authentication } = useAuth();
  const customerId =
    authentication.state === 'authenticated' ? authentication.user.customerUuid : null;
  const enabled = !skip && variables !== null && !!customerId;
  const variablesKey = JSON.stringify(variables);
  const key = useMemo(
    () => ({ document, variables, customerId, enabled }),
    // variablesKey gives semantically equal GraphQL variables one request identity.
    // oxlint-disable-next-line react/exhaustive-deps
    [document, variablesKey, customerId, enabled],
  );
  const [state, setState] = useState<{
    key: typeof key;
    data: TData | null;
    error: Error | null;
    loading: boolean;
  }>({ key, data: null, error: null, loading: enabled });
  const active = useRef<{ controller: AbortController; promise: Promise<void> } | null>(null);

  const refetch = useCallback((): Promise<void> => {
    if (!key.enabled || key.variables === null) return Promise.resolve();
    if (active.current) return active.current.promise;
    const controller = new AbortController();
    setState((previous) => ({
      key,
      data: previous.key === key ? previous.data : null,
      error: null,
      loading: true,
    }));
    let timedOut = false;
    const timeoutError = new Error('Mortgage request timed out');
    const request =
      failurePolicy === 'retry-and-report'
        ? requestZeusReadFromBrowser(key.document, key.variables, controller.signal)
        : requestZeusFromBrowser(key.document, key.variables, controller.signal);
    let timeoutId: number | undefined;
    const timeout = new Promise<never>((_, reject) => {
      timeoutId = window.setTimeout(() => {
        timedOut = true;
        controller.abort(timeoutError);
        reject(timeoutError);
      }, MORTGAGE_READ_TIMEOUT_MS);
    });
    const promise = Promise.race([request, timeout])
      .then((data) => {
        if (!controller.signal.aborted) setState({ key, data, error: null, loading: false });
        return undefined;
      })
      .catch((cause: unknown) => {
        if (controller.signal.aborted && !timedOut) return;
        let error = timeoutError;
        if (!timedOut) {
          error = cause instanceof Error ? cause : new Error('Mortgage request failed');
        }
        if (failurePolicy === 'retry-and-report') {
          captureError(redactGraphqlRequestErrorForTelemetry(error), 'mortgage-read');
        }
        setState({ key, data: null, error, loading: false });
      })
      .finally(() => {
        window.clearTimeout(timeoutId);
        if (active.current?.controller === controller) active.current = null;
      });
    active.current = { controller, promise };
    return promise;
  }, [failurePolicy, key]);

  useEffect(() => {
    void refetch();
    return () => {
      active.current?.controller.abort();
      active.current = null;
    };
  }, [refetch]);

  const current = enabled && state.key === key;
  return {
    data: current ? state.data : null,
    error: current ? state.error : null,
    loading: current
      ? state.loading
      : !skip && (enabled || authentication.state === 'authenticating'),
    refetch,
  };
}

export function useMortgageMutation<TData, TVariables extends Variables>(
  document: TypedDocumentString<TData, TVariables>,
) {
  const { authentication } = useAuth();
  const customerId =
    authentication.state === 'authenticated' ? authentication.user.customerUuid : null;
  const controllers = useRef(new Map<AbortController, string>());
  const documentRef = useRef(document);

  useEffect(() => {
    documentRef.current = document;
  }, [document]);

  useEffect(() => {
    const pending = controllers.current;
    return () =>
      pending.forEach((subject, controller) => {
        if (subject === customerId) controller.abort();
      });
  }, [customerId]);

  const mutate = useCallback(
    async (variables: TVariables): Promise<TData> => {
      if (!customerId) throw new Error('Not authenticated');
      const controller = new AbortController();
      controllers.current.set(controller, customerId);
      try {
        const result = await requestZeusFromBrowser(
          documentRef.current,
          variables,
          controller.signal,
        );
        controller.signal.throwIfAborted();
        return result;
      } finally {
        controllers.current.delete(controller);
      }
    },
    [customerId],
  );
  return { mutate };
}
